// SuperAdminScreen — 운영용 슈퍼어드민 콘솔(조회 전용).
// 1) 전체 유저 요약 카드 → 유저 선택 → 컬렉션 탭 → raw 문서 테이블(페이지네이션)
// 2) "이 사용자로 로그인" — 임퍼소네이션 토큰으로 그 유저로 완전 로그인(쓰기 포함).
import { ErrorState, LoadingState } from "@/components/States";
import { Icon, Pill, TableScroll, tdBase, thBase } from "@/components/ui";
import {
  type SuperadminCollectionPage,
  type SuperadminUser,
  getSuperadminCollection,
  getSuperadminUsers,
  mintImpersonationToken,
  setImpersonation,
} from "@/lib/api";
import { useIsMobile } from "@/lib/useViewport";
import { useEffect, useState } from "react";

const COLLECTIONS: { name: string; label: string }[] = [
  { name: "quotes", label: "견적" },
  { name: "customers", label: "고객사" },
  { name: "materials", label: "재료" },
  { name: "processes", label: "공정" },
  { name: "staff", label: "임직원" },
  { name: "cnc_raw_materials", label: "CNC 원자재" },
  { name: "cnc_surface_treatments", label: "CNC 표면처리" },
  { name: "cnc_labor_rates", label: "CNC 임율" },
  { name: "cnc_machine_expenses", label: "CNC 장비경비" },
  { name: "cnc_settings", label: "CNC 설정" },
  { name: "cost_master", label: "원가 마스터" },
  { name: "company", label: "회사 직인" },
];

const PAGE_SIZE = 50;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

// raw 값 → 셀 문자열. 날짜는 분 단위로 자르고, 객체/배열은 JSON 그대로.
function cellText(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "✓" : "—";
  if (typeof value === "number") return value.toLocaleString("ko-KR");
  if (typeof value === "string" && ISO_DATETIME.test(value))
    return value.slice(0, 16).replace("T", " ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export const SuperAdminScreen = ({ onToast }: { onToast: (msg: string) => void }) => {
  const mobile = useIsMobile();
  const [users, setUsers] = useState<SuperadminUser[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [collection, setCollection] = useState(COLLECTIONS[0].name);
  const [page, setPage] = useState(0);
  const [docs, setDocs] = useState<SuperadminCollectionPage | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [reloadReq, setReloadReq] = useState(0);
  // 발급된 임퍼소네이션 토큰(복붙용) — 카드의 "토큰 발급"으로 채워진다.
  const [issued, setIssued] = useState<{ name: string; email: string; token: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    getSuperadminUsers()
      .then((u) => alive && setUsers(u))
      .catch((e) => alive && setError(e instanceof Error ? e : new Error(String(e))));
    return () => {
      alive = false;
    };
  }, [reloadReq]);

  useEffect(() => {
    if (!selectedUid) return;
    let alive = true;
    setDocsLoading(true);
    getSuperadminCollection(selectedUid, collection, page * PAGE_SIZE, PAGE_SIZE)
      .then((d) => alive && setDocs(d))
      .catch(() => alive && setDocs(null))
      .finally(() => alive && setDocsLoading(false));
    return () => {
      alive = false;
    };
  }, [selectedUid, collection, page]);

  // 대상 유저의 임퍼소네이션 토큰을 발급받아 저장하고 앱을 새로고침 → 그 유저로
  // 완전 로그인된 상태로 재진입(전역 배너에서 복귀). 쓰기 포함.
  const loginAs = async (u: SuperadminUser) => {
    try {
      const t = await mintImpersonationToken(u.uid);
      setImpersonation({ token: t.token, uid: t.uid, email: t.email, name: t.name });
      window.location.reload();
    } catch {
      onToast("임퍼소네이션 토큰 발급 실패 — SUPERADMIN_KEY 설정을 확인하세요");
    }
  };

  // 토큰만 발급해 복붙용으로 화면에 표시(로그인 전환은 하지 않음). curl/자동화·
  // Playwright 에서 Authorization: Bearer 로 그대로 붙여 쓴다.
  const issueToken = async (u: SuperadminUser) => {
    try {
      const t = await mintImpersonationToken(u.uid);
      setIssued({ name: t.name, email: t.email, token: t.token });
      try {
        await navigator.clipboard.writeText(t.token);
        onToast(`토큰을 클립보드에 복사했습니다 (${Math.round(t.expires_in / 60)}분 유효)`);
      } catch {
        onToast("토큰을 발급했습니다 — 아래에서 복사하세요");
      }
    } catch {
      onToast("임퍼소네이션 토큰 발급 실패 — SUPERADMIN_KEY 설정을 확인하세요");
    }
  };

  if (error) return <ErrorState error={error} onRetry={() => setReloadReq((n) => n + 1)} />;
  if (!users) return <LoadingState />;

  const items = docs?.items ?? [];
  // 컬럼 = 현재 페이지 문서 키의 합집합(raw 뷰어 — 스키마 하드코딩 없음).
  const columns = [...new Set(items.flatMap((d) => Object.keys(d)))];
  const lastPage = docs ? Math.max(0, Math.ceil(docs.total / PAGE_SIZE) - 1) : 0;

  return (
    <div style={{ padding: mobile ? 14 : 28, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800 }}>슈퍼어드민</div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
          전체 유저의 등록 데이터를 조회하는 운영용 콘솔입니다. 조회 전용.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {users.map((u) => {
          const active = selectedUid === u.uid;
          return (
            <div
              key={u.uid}
              style={{
                background: "var(--bg-surface)",
                borderRadius: 16,
                padding: 18,
                boxShadow: active ? "inset 0 0 0 2px var(--brand)" : "none",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 800 }}>{u.name}</div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {u.email} · {u.role} · {u.position}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px 14px",
                  fontSize: 12,
                  color: "var(--text-dim)",
                }}
              >
                <span>견적 {u.counts.quotes ?? 0}</span>
                <span>고객사 {u.counts.customers ?? 0}</span>
                <span>재료 {u.counts.materials ?? 0}</span>
                <span>공정 {u.counts.processes ?? 0}</span>
                <span>임직원 {u.counts.staff ?? 0}</span>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                <Pill
                  variant={active ? "primary" : "dark"}
                  size="sm"
                  onClick={() => {
                    setSelectedUid(u.uid);
                    setPage(0);
                  }}
                >
                  데이터 조회
                </Pill>
                <Pill
                  variant="outlined"
                  size="sm"
                  icon={<Icon name="user" size={14} />}
                  onClick={() => loginAs(u)}
                >
                  이 사용자로 로그인
                </Pill>
                <Pill
                  variant="outlined"
                  size="sm"
                  icon={<Icon name="file" size={14} />}
                  onClick={() => issueToken(u)}
                >
                  토큰 발급
                </Pill>
              </div>
            </div>
          );
        })}
        {users.length === 0 && (
          <div style={{ fontSize: 13.5, color: "var(--text-muted)" }}>등록된 유저가 없습니다.</div>
        )}
      </div>

      {issued && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: 16,
            borderRadius: 12,
            background: "var(--bg-surface)",
            border: "1px solid var(--table-border-strong)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>
              {issued.name} ({issued.email}) 임퍼소네이션 토큰
            </span>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Authorization: Bearer 헤더에 사용 · 1시간 유효
            </span>
            <span style={{ flex: 1 }} />
            <Pill
              variant="dark"
              size="sm"
              onClick={() => {
                navigator.clipboard
                  .writeText(issued.token)
                  .then(() => onToast("토큰을 클립보드에 복사했습니다"))
                  .catch(() => onToast("복사 실패 — 아래 값을 직접 선택해 복사하세요"));
              }}
            >
              복사
            </Pill>
            <Pill variant="ghost" size="sm" onClick={() => setIssued(null)}>
              닫기
            </Pill>
          </div>
          <textarea
            readOnly
            value={issued.token}
            onFocus={(e) => e.currentTarget.select()}
            rows={3}
            style={{
              width: "100%",
              resize: "vertical",
              fontFamily: "monospace",
              fontSize: 12,
              lineHeight: 1.5,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid var(--ring-input)",
              background: "var(--bg-surface-2)",
              color: "var(--text-base)",
              wordBreak: "break-all",
            }}
          />
        </div>
      )}

      {selectedUid ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {COLLECTIONS.map(({ name, label }) => (
              <Pill
                key={name}
                variant={collection === name ? "primary" : "dark"}
                size="sm"
                onClick={() => {
                  setCollection(name);
                  setPage(0);
                }}
              >
                {label}
              </Pill>
            ))}
          </div>

          <TableScroll style={{ background: "var(--bg-surface)", borderRadius: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {columns.map((key) => (
                    <th key={key} style={{ ...thBase, textAlign: "left" }}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((doc, i) => (
                  <tr key={String(doc.id ?? doc.quote_id ?? doc.uid ?? i)}>
                    {columns.map((key) => (
                      <td
                        key={key}
                        title={cellText(doc[key])}
                        style={{
                          ...tdBase,
                          textAlign: "left",
                          maxWidth: 320,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {cellText(doc[key])}
                      </td>
                    ))}
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={Math.max(1, columns.length)}
                      style={{ ...tdBase, textAlign: "center", padding: "36px 0" }}
                    >
                      {docsLoading ? "불러오는 중…" : "데이터 없음"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroll>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: "var(--text-muted)",
            }}
          >
            <span>
              총 {docs?.total ?? 0}건{docsLoading ? " · 불러오는 중…" : ""}
            </span>
            <span style={{ flex: 1 }} />
            <Pill
              variant="outlined"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              이전
            </Pill>
            <Pill
              variant="outlined"
              size="sm"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => p + 1)}
            >
              다음
            </Pill>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13.5, color: "var(--text-muted)" }}>
          유저 카드에서 "데이터 조회"를 누르면 등록 데이터가 표시됩니다.
        </div>
      )}
    </div>
  );
};
